import { useEffect, useState } from "react";
import axios from "axios";
import { roleConfigs, scopeRecords, canVerify } from "./roleDashboardConfig";

export function getStoredUser() {
  try { return JSON.parse(localStorage.getItem("currentUser")) || {}; } catch { return {}; }
}

export async function fetchArray(url, signal) {
  const response = await axios.get(url, { signal });
  if (!Array.isArray(response.data)) throw new Error("Unexpected dashboard response");
  return response.data;
}

export default function useRoleDashboard(role) {
  const [state, setState] = useState({ loading: true, profile: null, data: {}, error: "" });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    const config = roleConfigs[role];
    setState({ loading: true, profile: null, data: {}, error: "" });
    async function load() {
      let profile = getStoredUser();
      let users = null;
      if (config.scope !== "group") {
        try {
          users = await fetchArray("/api/users/getallUsers", controller.signal);
          profile = users.find(user => user.username === profile.username);
          if (!profile?.companyName || (config.scope === "department" && !profile.department)) {
            throw new Error("Missing workspace");
          }
        } catch (error) {
          if (!controller.signal.aborted) setState({ loading: false, profile: null, data: {}, error: "Your company or department could not be loaded. Try again to load your workspace." });
          return;
        }
      }
      const requests = [
        ["assets", config.scope === "group" ? "/api/AssetRegisterDetails/getAssetDetails" : "/api/AssetRegisterDetails/getAssetDetails2"],
        ["pending", "/api/PendingAssetRegisterDetails/getPendingAssetDetails"],
      ];
      if (role === "audit") requests.push(["transfers", "/api/transfer/getPendingTransferAssetDetails"], ["discarded", "/api/PendingAsset/getPendingDiscardAsset"]);
      const results = await Promise.allSettled(requests.map(([, url]) => fetchArray(url, controller.signal)));
      if (controller.signal.aborted) return;
      const data = { users: scopeRecords(users, config, profile, true) };
      results.forEach((result, index) => {
        const key = requests[index][0];
        const records = result.status === "fulfilled" ? result.value : null;
        data[key] = scopeRecords(records, config, profile);
        if (key === "pending" && canVerify(role, profile)) {
          data.verification = scopeRecords(records, { scope: "company" }, profile);
        }
      });
      setState({ loading: false, profile, data, error: results.some(result => result.status === "rejected") ? "Some dashboard data could not be loaded. Unavailable values are shown as a dash." : "" });
    }
    load();
    return () => controller.abort();
  }, [role, attempt]);
  return { ...state, retry: () => setAttempt(value => value + 1) };
}
